import * as Sentry from '@sentry/react';
import { call, put, select } from 'redux-saga/effects';
import { acquireLease, getOne, releaseLease } from 'api/cloudsave/cloudsaveApi';
import { NetworkError, TimeoutError } from 'api/apiClient';
import { getCurrentUserId } from 'auth/userIdentity';
import { errorMessage } from 'containers/Snackbar/snackbarMessages';
import { getIsOnline } from 'store/connection/selectors';
import { setFileStatus } from 'store/fileState/reducer';
import { FileStatus } from 'store/fileState/types';
import type { RootState } from 'store';
import { withRetry } from 'store/sagaUtils';
import { addMessage } from 'store/snackbar/reducer';
import { requestConnectivityCheck } from 'util/connectivityChecks';
import { updateStorageUnitLeases } from './reducer';
import type {
  LeaseOnStorageUnitRequestActionInterface,
  PollStorageUnitLeasesRequestActionInterface,
} from './sagaActions';
import { leaseOnStorageUnitSuccess } from './sagaActions';
import type { Lease, StorageUnit, StorageUnitsReducerInterface } from './types';
import { LeaseAction, LeaseType } from './types';
import { evaluateLeaseState, normalizeLeases } from './utils';

export const areLeasesEqual = (a?: Lease | null, b?: Lease | null) => {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  const normalizeDate = (value?: Date) =>
    value ? new Date(value).getTime() : 0;
  return (
    a.userId === b.userId &&
    normalizeDate(a.leasedAt) === normalizeDate(b.leasedAt)
  );
};

export const areSoftLeasesEqual = (a: Lease[] = [], b: Lease[] = []) => {
  if (a.length !== b.length) {
    return false;
  }
  const sortByUser = (leases: Lease[]) =>
    [...leases].sort((left, right) => left.userId.localeCompare(right.userId));

  const normalizedA = sortByUser(a);
  const normalizedB = sortByUser(b);

  for (let index = 0; index < normalizedA.length; index++) {
    if (!areLeasesEqual(normalizedA[index], normalizedB[index])) {
      return false;
    }
  }

  return true;
};

export function* leaseOnStorageUnit(
  action: LeaseOnStorageUnitRequestActionInterface,
) {
  const { id, type, leaseType } = action.payload;
  const isOnline: boolean = yield select(getIsOnline);

  if (!isOnline) {
    return;
  }

  try {
    if (type === LeaseAction.Acquire) {
      const desiredType = leaseType ?? LeaseType.Soft;
      const { parsedBody } = yield withRetry(() =>
        acquireLease(id, desiredType),
      );
      const effectiveType = parsedBody?.leaseType ?? desiredType;
      yield put(leaseOnStorageUnitSuccess(id, type, effectiveType, parsedBody));
    } else if (type === LeaseAction.Release) {
      const currentUserId = getCurrentUserId();
      let releaseMode = leaseType;

      if (!releaseMode) {
        const storageUnitsState: StorageUnitsReducerInterface = yield select(
          (state: RootState) => state.storageUnitsState,
        );
        const {
          storageUnits,
          templates,
          archived,
          approvedFiles,
          currentFile,
        } = storageUnitsState;

        const targetUnit = [
          ...storageUnits,
          ...templates,
          ...approvedFiles,
          ...archived,
        ].find((unit) => unit.id === id);

        if (
          targetUnit?.exclusiveLease &&
          currentUserId &&
          targetUnit.exclusiveLease.userId === currentUserId
        ) {
          releaseMode = LeaseType.Exclusive;
        } else if (
          targetUnit?.softLeases &&
          currentUserId &&
          targetUnit.softLeases.some((lease) => lease.userId === currentUserId)
        ) {
          releaseMode = LeaseType.Soft;
        } else if (currentFile.id === id) {
          if (
            currentFile.exclusiveLease &&
            currentUserId &&
            currentFile.exclusiveLease.userId === currentUserId
          ) {
            releaseMode = LeaseType.Exclusive;
          } else if (
            currentUserId &&
            currentFile.softLeases.some(
              (lease) => lease.userId === currentUserId,
            )
          ) {
            releaseMode = LeaseType.Soft;
          }
        }
      }

      if (!releaseMode) {
        return;
      }

      yield withRetry(() => releaseLease(id));
      yield put(
        leaseOnStorageUnitSuccess(
          id,
          type,
          releaseMode,
          undefined,
          currentUserId ?? undefined,
        ),
      );
    } else {
      throw new Error('Error');
    }
  } catch (err) {
    if (err instanceof NetworkError || err instanceof TimeoutError) {
      yield call(requestConnectivityCheck);
      return;
    }

    Sentry.captureException(err, {
      tags: { saga: 'leaseOnStorageUnit' },
      extra: { storageUnitId: id, leaseAction: type, leaseType },
    });
    yield put(
      addMessage(
        errorMessage(
          `Failed to ${
            type === LeaseAction.Acquire ? 'acquire' : 'release'
          } lease`,
        ),
      ),
    );
  }
}

export function* pollStorageUnitLeases(
  action: PollStorageUnitLeasesRequestActionInterface,
) {
  const { id } = action.payload;

  try {
    const { parsedBody }: { parsedBody: StorageUnit } = yield getOne(id);
    const normalized = normalizeLeases(parsedBody);

    const storageUnitsState: StorageUnitsReducerInterface = yield select(
      (state: RootState) => state.storageUnitsState,
    );

    const collections = [
      storageUnitsState.storageUnits,
      storageUnitsState.templates,
      storageUnitsState.approvedFiles,
      storageUnitsState.archived,
    ];

    const collectionMatch = collections.reduce<StorageUnit | undefined>(
      (accumulator, collection) =>
        accumulator ?? collection.find((unit) => unit.id === id),
      undefined,
    );

    const currentFileMatch =
      storageUnitsState.currentFile.id === id
        ? storageUnitsState.currentFile
        : undefined;

    const existingExclusive =
      currentFileMatch?.exclusiveLease ?? collectionMatch?.exclusiveLease;
    const existingSoft =
      currentFileMatch?.softLeases ?? collectionMatch?.softLeases ?? [];

    const normalizedExclusive = normalized.exclusiveLease ?? undefined;
    const normalizedSoftLeases = normalized.softLeases ?? [];

    const exclusiveChanged = !areLeasesEqual(
      existingExclusive ?? null,
      normalizedExclusive ?? null,
    );
    const softChanged = !areSoftLeasesEqual(existingSoft, normalizedSoftLeases);

    if (!exclusiveChanged && !softChanged) {
      return;
    }

    yield put(
      updateStorageUnitLeases({
        id,
        exclusiveLease: normalizedExclusive,
        softLeases: normalizedSoftLeases,
      }),
    );

    if (storageUnitsState.currentFile.id === id) {
      const evaluation = evaluateLeaseState(
        normalized,
        FileStatus.EDIT,
        getCurrentUserId(),
      );
      const currentStatus: FileStatus = yield select(
        (state: RootState) => state.fileState.fileStatus,
      );

      if (evaluation.status !== currentStatus) {
        yield put(setFileStatus(evaluation.status));
      }
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { saga: 'pollStorageUnitLeases' },
      extra: { storageUnitId: id },
      level: 'warning',
    });
  }
}
